import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  type FacetedFilterOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  TransferGroupDescription,
  UserProfile,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import {
  formatLocalDateComponents,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import {
  RiBuilding2Line,
  RiCalendarLine,
  RiFilter3Line,
  RiPriceTagLine,
} from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import {
  transferGroupKindValues,
  transferGroupPageOrderValues,
} from "~/gql/client/enumValues";
import type {
  AdminTransferGroupRowFragment,
  TransferGroupFilter,
  TransferGroupKind,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/transfer-groups")({
  head: () => opsHead("Transfer Groups"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  graphql(`
    fragment AdminTransferGroupRow on TransferGroup {
      id
      shortRef
      createdAt
      kind
      status
      organisation {
        ...OrganisationCardFields
      }
      createdByUser {
        ...UserProfileUI
      }
      isAutoSweep
      assetRules {
        id
        evaluationCadence
      }
      totalVolume {
        currency
        amount
      }
      transfers {
        id
      }
      payouts {
        id
      }
      ...TransferGroupDescriptionFields
    }
  `);

  const transferGroupsDocument = graphql(`
    query AdminTransferGroups(
      $after: String,
      $first: Int!,
      $order: TransferGroupPageOrder,
      $filter: TransferGroupFilter!,
    ) {
      transferGroups(
        after: $after,
        first: $first,
        order: $order,
        filter: $filter,
      ) {
        pageInfo {
          endCursor
          hasNextPage
        }
        transferGroups {
          ...AdminTransferGroupRow
        }
      }
    }
  `);

  const [filterAndSortState, setFilterAndSortOrder] =
    useQueryPersistedStateWithSchema({
      useSearchParams,
      schema: z.object({
        endDateExclusive: z.date().optional().nullable(),
        kinds: z.array(z.enum(transferGroupKindValues)).optional().nullable(),
        organisationIds: z.array(z.string()).optional().nullable(),
        startDateInclusive: z.date().optional().nullable(),
        sortOrder: z.enum(transferGroupPageOrderValues).optional().nullable(),
      }),
      defaultState: {
        sortOrder: "CREATED_AT_DESC",
      },
    });

  const { sortOrder, ...filter } = filterAndSortState;

  const baseInput = {
    first: 20,
    order: sortOrder,
    filter,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    transferGroupsDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const groups = data?.transferGroups?.transferGroups ?? [];
  const nextCursor = data?.transferGroups?.pageInfo.hasNextPage
    ? (data.transferGroups.pageInfo.endCursor ?? undefined)
    : undefined;

  const { data: pageData } = useSuspenseQuery(
    graphql(`
      query AdminTransferGroupsPage {
        organisations {
          id
          name
        }
      }
    `),
  );

  const organisationOptions: FacetedFilterOption[] = (
    pageData?.organisations?.slice() ?? []
  )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({
      value: o.id,
      searchValues: [o.name],
      label: <div>{o.name}</div>,
    }));

  return (
    <AdminPageContainer testId="admin-transfer-groups">
      <AdminPageHeader
        title="Transfer Groups"
        subtitle="Groups of transfers and payouts."
      />
      <FilterBar
        filterValue={filter}
        onFilterChange={(values) => {
          setFilterAndSortOrder({
            ...values,
            sortOrder,
          });
        }}
        organisationOptions={organisationOptions}
      />
      <DataTable
        data={groups}
        columns={columns}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualSorting={false}
        manualPagination={true}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        cellClassName="text-xs p-2"
        onRowClick={(row) => {
          navigate({
            to: "/transfer-groups/$transferGroupId",
            params: { transferGroupId: row.original.id },
          });
        }}
        initialColumnVisibility={{
          id: false,
        }}
      />
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!groups.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}

const columns: ColumnDef<AdminTransferGroupRowFragment & { id: string }>[] = [
  {
    header: "ID",
    accessorKey: "id",
    cell: () => null,
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requested" />
    ),
    cell: ({ row }) => {
      const { shortDate, shortTime } = formatLocalDateComponents(
        row.original.createdAt,
      );

      return (
        <div className="flex flex-col">
          <div>{shortDate}</div>
          <div className="text-tertiary text-xs">{shortTime}</div>
        </div>
      );
    },
  },
  {
    id: "organisation",
    accessorFn: (row) => row.organisation?.name,
    header: "Organisation",
    cell: ({ row }) =>
      row.original.organisation ? (
        <OrganisationCard
          organisation={row.original.organisation}
          labelClassName="text-xs"
        />
      ) : null,
  },
  {
    id: "createdByUser",
    accessorFn: (row) => row.createdByUser?.firstName,
    header: "Created By",
    cell: ({ row }) => {
      if (row.original.createdByUser) {
        return <UserProfile {...row.original.createdByUser} />;
      }
      if (row.original.isAutoSweep) {
        return <span className="text-tertiary text-xs">Sweep</span>;
      }
      if (row.original.assetRules && row.original.assetRules.length > 0) {
        const cadence = row.original.assetRules[0].evaluationCadence;
        return (
          <span className="text-tertiary text-xs">
            Asset rule: {prettyPrintEnum(cadence)}
          </span>
        );
      }
      return null;
    },
  },
  {
    id: "description",
    accessorFn: () => undefined,
    header: "Description",
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-tertiary text-xs uppercase">
          {transferGroupKindLabel(row.original.kind)}
        </span>
        <TransferGroupDescription transferGroup={row.original} />
      </div>
    ),
  },
  {
    id: "totalVolume",
    accessorFn: () => undefined,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Total Volume"
        align="right"
        className="flex flex-row justify-end pr-2"
      />
    ),
    cell: ({ row }) => {
      if (!row.original.totalVolume) {
        return null;
      }
      const { amount, currency } = row.original.totalVolume;
      return (
        <div className="flex w-full items-center justify-end gap-2">
          <CurrencyAmountUI amount={amount} currency={currency} />(
          {(row.original.transfers?.length ?? 0) +
            (row.original.payouts?.length ?? 0)}
          )
        </div>
      );
    },
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      if (!row.original.status) {
        return null;
      }
      return <StatusUI status={row.original.status} />;
    },
  },
];

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
}: {
  filterValue: TransferGroupFilter;
  onFilterChange: (_: TransferGroupFilter) => void;
  organisationOptions: FacetedFilterOption[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");

  const filterCount =
    0 +
    ((filterValue?.organisationIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.kinds?.length ?? 0) > 0 ? 1 : 0);

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      startDateInclusive: undefined,
      endDateExclusive: undefined,
      organisationIds: undefined,
      kinds: undefined,
    });
  };

  const kindOptions = transferGroupKindValues.map((k) => ({
    value: k,
    label: <span>{transferGroupKindLabel(k)}</span>,
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Date</div>
                {(filterValue.endDateExclusive ||
                  filterValue.startDateInclusive) && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("kind")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Kind</div>
                {(filterValue.kinds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("org")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateInclusive: value?.startAtInclusive,
                    endDateExclusive: value?.endAtExclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive: filterValue.startDateInclusive ?? undefined,
                  endAtExclusive: filterValue.endDateExclusive ?? undefined,
                }}
              />
            )}
            {path === "kind" && (
              <ControlledFacetedFilterControl
                options={kindOptions}
                selectedValues={filterValue.kinds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    kinds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "org" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues.filter((id) => id !== null)
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateInclusive ?? undefined,
          endAtExclusive: filterValue.endDateExclusive ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Kind"
        options={kindOptions}
        selectedValues={filterValue.kinds ?? []}
        onClick={() => {
          setPath("kind");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("org");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}

function transferGroupKindLabel(kind: TransferGroupKind): string {
  switch (kind) {
    case "REBALANCE":
      return "Rebalance";
    case "REBALANCE_WITHDRAWAL":
      return "Withdrawal (Rebalance)";
    case "REBALANCE_PAYMENT":
      return "Payment (Rebalance)";
    case "TRANSFER_INTERNAL":
      return "Internal transfer";
    case "TRANSFER_WITHDRAWAL":
      return "Withdrawal";
    default:
      return neverFunc(kind);
  }
}
